import React, { useMemo, useState } from 'react'
import styles from './socialMatchesPanels.module.scss'
import classNames from 'classnames'
import { TabContent, TabNavigationModule, TabObj } from 'components/generic/TabNavigation'
import i18n from 'constants/strings'
import { MatchUpSubmissionStateEnum } from 'types/api/enums'
import { useDispatch, useSelector } from 'react-redux'
import { AppState } from 'redux/reducers'
import { withModals } from 'hooks/use-modal'
import ActionPendingScoreModal, {
  ActionPendingScoreModalType,
} from '../modals/ActionPendingScoreModal'
import { ProfileModalId } from 'constants/modals'
import SocialMatchesContainer from '../SocialMatchesContainer'
import { MY_SOCIAL_MATCHES_KEY, Paging, SocialMatchUpUI } from 'types/global'
import { getMySocialMatches } from 'actions/social-matches-actions'
import InfoPendingScoreModal from '../modals/InfoPendingScoreModal'
import Panel from 'components/generic/containers/Panels'
import NoScoresPanel from './NoScoresPanel'
import LoadingSpinnerWithContainer from 'components/generic/LoadingSpinnerWithContainer'

const MyScoresPanel = () => {
  const dispatch = useDispatch()
  const totalsSocialMatches = useSelector(
    (state: AppState) => state.socialMatches.totals[MY_SOCIAL_MATCHES_KEY]
  )
  const totalPending = useSelector(
    (state: AppState) => state.socialMatches.totals[MY_SOCIAL_MATCHES_KEY]?.PENDING
  )
  const [activeTab, setActiveTab] = useState<MatchUpSubmissionStateEnum>(
    MatchUpSubmissionStateEnum.APPROVED
  )

  const hasSocialMatches = useMemo(() => {
    if (!totalsSocialMatches) return false
    return !!Object.values(totalsSocialMatches).reduce((acc, val) => {
      return acc + val
    }, 0)
  }, [totalsSocialMatches])

  const fetch = useMemo(() => {
    return {
      approved: ({ skip, limit }: Paging) =>
        dispatch(getMySocialMatches(MatchUpSubmissionStateEnum.APPROVED, { skip, limit })),
      pending: ({ skip, limit }: Paging) =>
        dispatch(getMySocialMatches(MatchUpSubmissionStateEnum.PENDING, { skip, limit })),
      rejected: ({ skip, limit }: Paging) =>
        dispatch(getMySocialMatches(MatchUpSubmissionStateEnum.REJECTED, { skip, limit })),
    }
  }, [dispatch])
  const NAV_TABS: TabObj[] = useMemo(
    () => [
      {
        id: MatchUpSubmissionStateEnum.APPROVED,
        key: MatchUpSubmissionStateEnum.APPROVED,
        title: i18n.t('strSocialMatches.myScores.submitted'),
      },
      {
        id: MatchUpSubmissionStateEnum.PENDING,
        key: MatchUpSubmissionStateEnum.PENDING,
        title: (
          <>
            {i18n.t('strSocialMatches.myScores.pending')}
            {totalPending > 0 && <span className={styles.counter}>{totalPending}</span>}
          </>
        ),
      },
      {
        id: MatchUpSubmissionStateEnum.REJECTED,
        key: MatchUpSubmissionStateEnum.REJECTED,
        title: i18n.t('strSocialMatches.myScores.rejected'),
      },
    ],
    [totalPending]
  )

  return (
    <Panel
      className={classNames(styles.panel, styles.myScores)}
      hasShadow
      fullWidthAtTablet
      as='section'
    >
      <h2 className={styles.title}>{i18n.t('strSocialMatches.myScores.title')}</h2>
      {!totalsSocialMatches && <LoadingSpinnerWithContainer containerHeight={300} />}
      {totalsSocialMatches && hasSocialMatches ? (
        <>
          <TabNavigationModule
            navId={'my-scores-tabs'}
            activeTabKey={activeTab}
            tabs={NAV_TABS}
            onTabSelect={key => setActiveTab(key as MatchUpSubmissionStateEnum)}
            navColorScheme='secondary'
            tabStyle='chip'
            containerClass={styles.tabsContainerWrapper}
            navClass={styles.tabsContainer}
          >
            <TabContent tabKey={MatchUpSubmissionStateEnum.APPROVED} activeTabKey={activeTab}>
              <SocialMatchesContainer
                type={MatchUpSubmissionStateEnum.APPROVED}
                fetch={fetch.approved}
                stateMappingKey={MY_SOCIAL_MATCHES_KEY}
                renderIfNoMatches={
                  <NoScoresPanel
                    title={i18n.t('strSocialMatches.myScores.noScoresPanel.approved.title')}
                    info={i18n.t('strSocialMatches.myScores.noScoresPanel.approved.info')}
                    className={styles.noScorePanel}
                  />
                }
              />
            </TabContent>
            <TabContent tabKey={MatchUpSubmissionStateEnum.PENDING} activeTabKey={activeTab}>
              <p className={styles.infoText}>{i18n.t('strSocialMatches.myScores.info.pending')}</p>
              <SocialMatchesContainer
                type={MatchUpSubmissionStateEnum.PENDING}
                fetch={fetch.pending}
                stateMappingKey={MY_SOCIAL_MATCHES_KEY}
                renderIfNoMatches={
                  <NoScoresPanel
                    title={i18n.t('strSocialMatches.myScores.noScoresPanel.pending.title')}
                    info={i18n.t('strSocialMatches.myScores.noScoresPanel.pending.info')}
                    className={styles.noScorePanel}
                  />
                }
              />
            </TabContent>
            <TabContent tabKey={MatchUpSubmissionStateEnum.REJECTED} activeTabKey={activeTab}>
              <p className={styles.infoText}>{i18n.t('strSocialMatches.myScores.info.rejected')}</p>
              <SocialMatchesContainer
                type={MatchUpSubmissionStateEnum.REJECTED}
                fetch={fetch.rejected}
                stateMappingKey={MY_SOCIAL_MATCHES_KEY}
                renderIfNoMatches={
                  <NoScoresPanel
                    title={i18n.t('strSocialMatches.myScores.noScoresPanel.rejected.title')}
                    info={i18n.t('strSocialMatches.myScores.noScoresPanel.rejected.info')}
                    className={styles.noScorePanel}
                  />
                }
              />
            </TabContent>
          </TabNavigationModule>
          <ActionPendingScoreModal />
          <InfoPendingScoreModal />
        </>
      ) : totalsSocialMatches && !hasSocialMatches ? (
        <NoScoresPanel
          title={i18n.t('strSocialMatches.myScores.noScoresPanel.getStarted.title')}
          info={i18n.t('strSocialMatches.myScores.noScoresPanel.getStarted.info')}
        />
      ) : null}
    </Panel>
  )
}

export const modalsState = {
  modals: {
    [ProfileModalId.ACTION_PENDING_SCORE]: {
      isOpen: false,
      type: ActionPendingScoreModalType.APPROVE,
      matchId: '',
      match: null as null | SocialMatchUpUI,
    },
    [ProfileModalId.INFO_PENDING_SCORE]: {
      isOpen: false,
    },
  },
}

export type ProfileModalsState = typeof modalsState.modals

export default withModals(MyScoresPanel, modalsState)
